/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link } from "@packages/framework";
import type { CommunitiesAdminMostPopular } from "@packages/sdk";

import { numericPercentages, spacing } from "../../../../../../global/emotion";
import { useCarousel } from "../../../../../hooks/useCarousel";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import { ContentMedium } from "../../../../_base/content/content/ContentMedium/ContentMedium.emotion";
import { VideoIcon } from "../../../../_base/icons/Icon/GeneralIcons";
import { Carousel } from "../../../layout/Carousel/Carousel.emotion";

const LOADING_SKELETONS_TOTAL = 8;

const carouselStyle: Interpolation = {
  maxWidth: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: `calc(100vw - ${spacing.sidebar})`,
  },
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
};

export type CarouselCommunityMostPopularProps = {
  data: CommunitiesAdminMostPopular;
  id: number;
  loading?: boolean;
};

export const CarouselCommunityMostPopular = ({
  data,
  id,
  loading = false,
}: CarouselCommunityMostPopularProps) => {
  const carouselApi = useCarousel({
    pageOffset: [
      [24, 32],
      [24, 24],
    ],
    size: [2, 2, 3, 4, 5, 5],
  });

  return (
    <Carousel carouselApi={carouselApi} containerCSS={carouselStyle}>
      {loading
        ? [...Array(LOADING_SKELETONS_TOTAL)].map((index) => (
            <ContentMedium key={index} loading />
          ))
        : data?.results?.map((mostPopular, index) => {
            const title = mostPopular.name ?? mostPopular.title;
            const description =
              mostPopular.short_desc ??
              mostPopular.label_desc ??
              mostPopular.description;
            const metadata =
              mostPopular?.lengths ?? mostPopular.sessions ?? null;
            const href = `/admin/${id}/dashboard/${
              mostPopular.sessions
                ? `collections/${mostPopular.id}`
                : `prayers/${mostPopular.id}`
            }`;
            return (
              <ContentMedium
                asChild
                description={description}
                imgSrc={mostPopular.images}
                metadata={metadata}
                metadataInlineIcon={
                  mostPopular.has_video ? <VideoIcon.Filled /> : null
                }
                rank={index + 1}
                title={title}
              >
                <Link href={href} />
              </ContentMedium>
            );
          })}
    </Carousel>
  );
};
